import * as React from 'react'
import styled, { css } from 'styled-components'
import { Colors } from '../constants'
import { Debounce } from '../debounce'
import keyboard, { KeyAction } from '../keyboard'
import memoize, { MemoizeStore } from '../memoize'
import { IntroTextSpan, InputMed } from './core'

interface TypeaheadBoxProps<T> {
  innerRef?: React.RefObject<HTMLInputElement>
  inputElementId?: string
  /**
   * Loads top results for a search prefix. The results can be arbitrary data models.
   */
  loadFunc: (prefix: string) => Promise<T[]>
  /**
   * Converts a result model into an element, displayed inside of <RowRoot> as a single search result row.
   */
  rowFunc: (val: T, prefix: string) => JSX.Element
  /**
   * Return true to prevent blur (user clicked a dropdown option, but stays in the typeahead).
   */
  onChoose: (val: T) => boolean | void
  /**
   * Called when the typeahead box receives focus.
   */
  onFocus?: () => void
  /**
   * Called when the typeahead box loses focus.
   */
  onBlur?: () => void
  /**
   * Display this as the input text after a row has been selected
   */
  inputTextOnSelect?: (val: T) => string
  /**
   * Optionally listens for a keyboard shortcut action, focuses the typeahead box when detected. See keyboard.ts
   *
   * The escape (esc) action always gives up focus.
   */
  focusAction?: KeyAction | null
  /**
   * Placeholder text. Usually a short prompt like "Search...", "Filter...", or an example like "Jane Doe"
   */
  placeholder?: string | null
  /**
   * If set, the entire page will darken when the typeahead search is active. Useful for omnibox nav, etc.
   * If the point of the typeahead is to *take you to a different page*, then darken the current page while searching.
   */
  fadeBackground?: boolean | null
  /**
   * If set, the typeahead search will call loadFunc("") and display the results, before any user interaction.
   */
  showResultsWhenEmpty?: boolean | null
  /**
   * If set, the typeahead box will grab focus on mount. Useful on eg. a Search or FAQ page.
   * If most visits to a given page lead directly into a search, then just start the cursor in the search box.
   */
  autoFocus?: boolean
  /**
   * Specify a custom input box. Defaults to InputMed.
   */
  inputComponent?: any
  /**
   * Block vs inline-block
   */
  inline?: boolean
  /**
   * Background z-index. Defaults to 1.
   *
   * Fade background at this z, typeahead box and results at z+1
   */
  backgroundZIndex?: number
  /**
   * Whether to clear the input when blurred.
   *
   * @default true
   */
  clearOnBlur?: boolean
  /**
   * Whether to show feedback that the input was not valid.
   */
  hasError?: boolean
  /**
   * whether or not to debounce the loading of results once the prefix changes.
   */
  debounceResultLoading?: boolean
}

interface TypeaheadBoxState<T> {
  focused: boolean
  prefix?: string
  selectedIndex: number
  results?: T[]
}

export default class TypeaheadBox<T> extends React.PureComponent<TypeaheadBoxProps<T>, TypeaheadBoxState<T>> {
  private mouseOn = false
  private inputRef: React.RefObject<HTMLInputElement>
  private debouncedResultsLoader = new Debounce(this.freshLoadPrefix.bind(this), 500)

  constructor(props: TypeaheadBox<T>['props']) {
    super(props)
    const results: T[] | undefined = undefined
    this.state = {
      focused: false,
      prefix: '',
      selectedIndex: 0,
      results,
    }
    this.inputRef = props.innerRef || React.createRef()
  }

  componentDidMount() {
    keyboard.addListener(this.onKeyboardBound)
    this.loadPrefix('')
  }

  componentWillUnmount() {
    keyboard.removeListener(this.onKeyboardBound)
  }

  render() {
    const { placeholder, rowFunc, fadeBackground, showResultsWhenEmpty, autoFocus, inputElementId, hasError } =
      this.props
    const { focused, prefix, results, selectedIndex } = this.state

    const showResults = (showResultsWhenEmpty || prefix!.length > 0) && focused

    const InputComp = this.props.inputComponent || TypeaheadInput

    const display = this.props.inline ? 'inline-block' : 'block'

    const z1 = this.props.backgroundZIndex == null ? 1 : this.props.backgroundZIndex
    const z2 = z1 + 1

    return (
      <div style={{ display }}>
        {showResults && fadeBackground && <TypeaheadBackground style={{ zIndex: z1 }} />}
        <TypeaheadForeground style={{ zIndex: z2 }}>
          <InputComp
            ref={this.inputRef}
            id={inputElementId}
            placeholder={placeholder || 'Search...'}
            value={prefix}
            onKeyDown={this.onKeyDownBound}
            onChange={this.onChangeBound}
            onFocus={this.onFocusBound}
            onBlur={this.onBlurBound}
            autoFocus={autoFocus}
            hasError={hasError}
            autoComplete={'off'} // disable autocomplete by default, see: #12276
          />
          {showResults && (
            <TypeaheadResults
              inputHeight={this.inputRef.current ? this.inputRef.current.offsetHeight : 36}
              zIndex={z2}
              prefix={prefix ? prefix.trim() : ''}
              results={results}
              rowFunc={rowFunc}
              selectedIndex={selectedIndex}
              onMouseOn={this.onMouseOnBound}
              onMouseOff={this.onMouseOffBound}
              onClick={this.onClickBound}
            />
          )}
        </TypeaheadForeground>
      </div>
    )
  }

  private onMouseOnBound = () => {
    this.mouseOn = true
  }

  private onMouseOffBound = () => {
    this.mouseOn = false
  }

  private onClickBound = (ix: number) => {
    this.mouseOn = false
    if (!this.chooseIndex(ix)) {
      this.onBlurBound()
    }
  }

  private chooseIndex(ix: number): boolean {
    const { inputTextOnSelect } = this.props
    const { results } = this.state
    if (results == null || results[ix] == null) {
      return false
    }
    const val = results[ix]

    /**
     * If we are provided a function to map the selected item to a string, use that to
     * decide what to display in the box.
     *
     * Otherwise, TypeaheadBox is generic, meaning its results can be anything. We special case a
     * typeaheadbox that is for strings, so that clicking on a result completes what you
     * typed in to that value. If the results are something else, like an object,
     * DON'T do that.
     */
    if (inputTextOnSelect != null) {
      this.setState({ prefix: inputTextOnSelect(val) })
    } else if (typeof val === 'string') {
      this.setState({ prefix: val })
    }

    return this.props.onChoose(val) || false
  }

  private onKeyboardBound = (action: KeyAction) => {
    const { focusAction } = this.props
    if (action === focusAction && this.inputRef.current) {
      this.inputRef.current.focus()
    }
  }

  private onKeyDownBound = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Escape' && this.inputRef.current) {
      this.inputRef.current.blur()
    }

    const { results, selectedIndex } = this.state
    if (results != null) {
      if (e.key === 'ArrowUp') {
        const newSelectedIndex = Math.max(0, selectedIndex - 1)
        this.setState({ selectedIndex: newSelectedIndex })
      } else if (e.key === 'ArrowDown') {
        const newSelectedIndex = Math.min(results.length - 1, selectedIndex + 1)
        this.setState({ selectedIndex: newSelectedIndex })
      } else if (e.key === 'Enter') {
        e.preventDefault()
        if (!this.chooseIndex(selectedIndex) && this.inputRef.current) {
          this.inputRef.current.blur()
        }
      }
    }
  }

  private onFocusBound = () => {
    const { onFocus } = this.props
    if (onFocus) {
      onFocus()
    }

    this.setState({ focused: true })
  }

  private onBlurBound = () => {
    if (this.mouseOn) {
      return
    }

    const { onBlur } = this.props
    if (onBlur) {
      onBlur()
    }

    this.setState({ selectedIndex: 0, focused: false })

    if (this.props.clearOnBlur !== false) {
      this.setState({ prefix: '' })
      this.loadPrefix('')
    }
  }

  private onChangeBound = (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.target
    const prefix = target.value || ''

    this.loadPrefix(prefix)
  }

  private loadPrefix(prefix: string) {
    const { showResultsWhenEmpty } = this.props
    if (prefix.length > 0 || showResultsWhenEmpty) {
      // remove spaces if prefix is a phone number, e.g. (123) 222-2345 -> (123)222-2345
      const noSpaceInPhoneNumber = prefix.replace(/(\(\d{3}\))\s+(\d{3})\s*-\s*(\d{4})/, '$1$2-$3')
      this.setState({ prefix: noSpaceInPhoneNumber, selectedIndex: 0, results: undefined })

      // Fetch results for this prefix
      if (this.props.debounceResultLoading) {
        this.debouncedResultsLoader.exec(noSpaceInPhoneNumber)
      } else {
        this.freshLoadPrefix(noSpaceInPhoneNumber)
      }
    } else {
      // Empty prefix, no results
      this.setState({ prefix, selectedIndex: 0, results: undefined })
    }
  }

  private freshLoadPrefix(prefix: string) {
    this.props
      .loadFunc(prefix)
      .then(results => {
        if (prefix !== this.state.prefix) {
          return // we don't want to show irrelevant results
        }
        this.onResults(prefix, results)
      })
      .catch(err => {
        console.warn('TypeaheadBox loader failed', err)
        this.onResults(prefix, undefined)
      })
  }

  private onResults(prefix: string, results?: any[]) {
    if (prefix !== this.state.prefix) {
      // Discard stale result
      return
    }
    if (!this.inputRef.current) {
      // Unmounted, discard
      return
    }

    const { selectedIndex } = this.state
    if (results == null || selectedIndex >= results.length) {
      this.setState({ selectedIndex: 0, results })
    } else {
      this.setState({ results })
    }
  }
}

class TypeaheadResults extends React.PureComponent<{
  prefix: string
  results?: any[]
  rowFunc: (val: any, prefix: string) => JSX.Element
  selectedIndex: number
  onMouseOn: () => void
  onMouseOff: () => void
  onClick: (ix: number) => void
  zIndex: number
  inputHeight: number
}> {
  private clickHandlers: MemoizeStore<never> = {}

  render() {
    const { prefix, results, rowFunc, selectedIndex, onMouseOn, onMouseOff, onClick } = this.props

    const { zIndex, inputHeight } = this.props
    const rowStyle = { zIndex }
    const rootStyle = { top: `${inputHeight + 8}px` }

    let body = null
    if (results == null) {
      body = (
        <RowRoot style={rowStyle} isSelected={false}>
          <IntroTextSpan>Loading...</IntroTextSpan>
        </RowRoot>
      )
    } else if (results.length === 0) {
      body = (
        <RowRoot style={rowStyle} isSelected={false}>
          <IntroTextSpan>No results</IntroTextSpan>
        </RowRoot>
      )
    } else {
      body = results.map((obj, ix) => {
        const rowBody = rowFunc(obj, prefix)
        return (
          <RowRoot
            style={rowStyle}
            key={rowBody.key || ix}
            isSelected={ix === selectedIndex}
            onMouseDown={onMouseOn}
            onMouseLeave={onMouseOff}
            onClick={memoize(this.clickHandlers, ix, (index: number) => () => onClick(index))}
          >
            {rowBody}
          </RowRoot>
        )
      })
    }
    return <ResultsRoot style={rootStyle}>{body}</ResultsRoot>
  }
}

const TypeaheadBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.5);
`

const TypeaheadForeground = styled.div`
  width: 100%;
  position: relative;
`

const ResultsRoot = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  border-radius: 4px;
  overflow: hidden;
`

const RowRoot = styled.div`
  ${(props: { isSelected?: boolean }) => css`
    padding: 10px 16px;
    background-color: ${props.isSelected ? Colors.charcoal : Colors.ink};
    border-bottom: 1px solid ${Colors.charcoal};
    cursor: pointer;
  `};
`

const TypeaheadInput = styled(InputMed)`
  width: 100%;
`
